import { useEffect } from 'react';
import useSessionStorage, { SessionStorageKey } from './useSessionStorage';

export enum ReferralParamsKey {
  REF = 'ref',
  UTM_ID = 'utm_id',
  UTM_SOURCE = 'utm_source',
  UTM_MEDIUM = 'utm_medium',
  UTM_CAMPAIGN = 'utm_campaign',
  UTM_TERM = 'utm_term',
  UTM_CONTENT = 'utm_content',
  CRM_ACQ_TYPE = 'crmAcqType',
  CRM_SOURCE_TYPE = 'crmSourceType',
  ODOO_CUSTOMER_ID = 'odooCustomerId',
}

export type ReferralParams = {
  [K in ReferralParamsKey]?: string;
} & {
  [key: string]: string | undefined;
};

const useReferralParams = (): ReferralParams => {
  const [referralParams, setReferralParams] = useSessionStorage<ReferralParams>(SessionStorageKey.CAMPAIGN_SOURCE, {});

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const newParams: ReferralParams = {};
    let hasParams = false;

    const refValue = urlParams.get(ReferralParamsKey.REF);
    if (refValue) {
      newParams['REF'] = refValue;
      hasParams = true;
    }

    const utmMap = {
      id: 'UTM_ID',
      source: 'UTM_SOURCE',
      medium: 'UTM_MEDIUM',
      campaign: 'UTM_CAMPAIGN',
      term: 'UTM_TERM',
      content: 'UTM_CONTENT',
    };

    Object.entries(utmMap).forEach(([param, enumKey]) => {
      const value = urlParams.get(`utm_${param}`);
      if (value) {
        newParams[enumKey] = value;
        hasParams = true;
      }
    });

    urlParams.forEach((value, key) => {
      if (key.startsWith('utm_') && !Object.values(ReferralParamsKey).includes(key as ReferralParamsKey)) {
        newParams[key] = value;
        hasParams = true;
      }
    });

    const crmParamsMap = {
      crmAcqType: 'CRM_ACQ_TYPE',
      crmSourceType: 'CRM_SOURCE_TYPE',
    };

    Object.entries(crmParamsMap).forEach(([paramKey, enumKey]) => {
      const value = urlParams.get(paramKey);
      if (value) {
        newParams[enumKey] = value;
        hasParams = true;
      }
    });

    const odooCustomerId = urlParams.get(ReferralParamsKey.ODOO_CUSTOMER_ID);
    if (odooCustomerId) {
      newParams['ODOO_CUSTOMER_ID'] = odooCustomerId;
      hasParams = true;
    }

    if (hasParams) {
      setReferralParams({
        ...referralParams,
        ...newParams,
      });
    }
  }, []);

  return referralParams;
};

export default useReferralParams;
